import { Component, OnInit, ViewChild, inject } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { CerrixPromptService } from "@app/shared/services/cerrix-prompt.service";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { AiSettingsService } from "@app/ai/services/ai-settings-data.service";
import { AiSettingConfig } from "@app/ai/shared/models/AiSettingConfig";
import { AiSettingsModel } from "@app/ai/shared/models/AiSettingModel";
import { AiSettingsType } from "@app/ai/shared/enums/AiSettingsType";
import { DateTimeFormats, LocalizationService } from "@app/shared";
import { firstValueFrom } from "rxjs";
import { CerrixLoaderComponent } from "@app/shared/components/cerrix-loader/cerrix-loader.component";
import { MenuSystemDirective } from "@app/shared/directives/menu-system.directive";
import { CerrixToggleInfoComponent } from "@app/shared/cerrix-toggle-info/cerrix-toggle-info.component";
import { CerrixSelectComponent } from "@app/shared/cerrix-select/cerrix-select.component";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "@app/ai/i18n/scope";

@Component({
    selector: "ai-settings",
    templateUrl: "./ai-settings.component.html",
    styleUrls: ["./ai-settings.component.scss"],
    imports: [
        CerrixLoaderComponent,
        MenuSystemDirective,
        CerrixToggleInfoComponent,
        CerrixSelectComponent,
        TranslatePipe,
    ],
    viewProviders: [provideTranslationScope(scope)],
})
export class AiSettingsComponent implements OnInit {
    private toastr = inject(ToastrService);
    private aiSettingsService = inject(AiSettingsService);
    private locService = inject(LocalizationService);
    promptService = inject(CerrixPromptService);
    private _translation = injectTranslation(scope);

    @ViewChild("textPopupTemplate", { static: true }) textPopupTemplate: any;

    tab: TabModel;

    settingsConfig: AiSettingConfig[] = [];

    async ngOnInit() {
        if (!this.tab.showLoader) {
            this.tab.showLoader = true;
        }

        // The template stays behind the loader until the settings load, but the setting
        // descriptions and the error toast are translated before that. A failed request
        // must not reach the toast before the scope has loaded.
        await this._translation.ready();

        Promise.all([
            firstValueFrom(this.aiSettingsService.getSettings()),
            firstValueFrom(this.aiSettingsService.getOutputLanguages()),
        ])
            .then(([settings, outputLanguages]) => {
                this.outputLanguageOptions = outputLanguages.map((l) => ({
                    ID: l.isoCode,
                    Name: l.name,
                }));
                this.parseSettings(settings);
            })
            .catch(() => {
                this.toastr.error(this._translation.t("settings.loadFailed"));
            })
            .finally(() => {
                this.tab.showLoader = false;
            });
    }

    private parseSettings(settings: AiSettingsModel[]) {
        this.settingsConfig = settings.map((setting) => {
            const config = <AiSettingConfig>{
                id: setting.id,
                name: setting.name,
            };

            this.setSpecificProperties(config, setting);

            return config;
        });
    }

    private setSpecificProperties(config: AiSettingConfig, setting: AiSettingsModel) {
        switch (config.id) {
            case AiSettingsType.EnableAiFeatures: {
                this.setEnableAiFeaturesConfig(config, setting);
                break;
            }
            case AiSettingsType.OutputLanguage: {
                this.setOutputLanguageConfig(config, setting);
                break;
            }
        }
    }

    public showTermsAndConditions() {
        const promptConfig = this.getTermsAndConditionsPromptConfig();
        promptConfig.data.title = this._translation.t("settings.termsTitle");
        promptConfig.data.confirmButton.show = false;

        this.promptService.confirmCustom(promptConfig);
    }

    public beforeChangeEnableAiFeatures = async (currentValue: boolean) => {
        if (currentValue) {
            return true;
        }

        const result = await this.promptService
            .confirmCustom(this.getTermsAndConditionsPromptConfig())
            .toPromise();

        return !!result;
    };

    private setEnableAiFeaturesConfig(config: AiSettingConfig, setting: AiSettingsModel) {
        config.isToggle = true;
        config.boolValue = setting.boolValue;
        config.description = this._translation.t("settings.enableAiDescription");
        config.descriptionClick = this.showTermsAndConditions.bind(this);

        if (config.boolValue === true) {
            const formattedDateTime = this.locService.formatDateByEnum(
                setting.updatedOn,
                DateTimeFormats.DateShortTime,
            );
            config.toggleInfo = this._translation.t("settings.enabledAndAcceptedInfo", {
                updatedBy: setting.updatedBy,
                dateTime: formattedDateTime,
            });
        }

        config.beforeChange = this.beforeChangeEnableAiFeatures;
        config.valueChange = this.aiFeaturesToggleChanged.bind(this);
    }

    private getTermsAndConditionsPromptConfig() {
        return {
            maxWidth: "800px",
            maxHeight: "480px",
            data: {
                title: this._translation.t("settings.acceptTermsTitle"),
                message: "",
                customTemplate: this.textPopupTemplate,
                cancelButton: {
                    text: this._translation.t("settings.closeButton"),
                },
                confirmButton: {
                    text: this._translation.t("settings.acceptAndEnableButton"),
                    show: true,
                },
            },
        };
    }

    async aiFeaturesToggleChanged(newValue: boolean) {
        if (this.tab.showLoader) {
            return;
        }

        await firstValueFrom(
            this.aiSettingsService.storeSetting({
                id: AiSettingsType.EnableAiFeatures,
                boolValue: newValue,
            }),
        );

        this.toastr.success(
            this._translation.t("settings.aiFeaturesToggled", {
                state: newValue ? "enabled" : "disabled",
            }),
        );

        this.tab.refresh();
    }

    private outputLanguageOptions: { ID: string; Name: string }[] = [];

    private setOutputLanguageConfig(config: AiSettingConfig, setting: AiSettingsModel) {
        config.isToggle = false;
        config.isDropdown = true;
        config.description = this._translation.t("settings.outputLanguageDescription");
        config.dropdownOptions = this.outputLanguageOptions;
        config.dropdownValue = setting.stringValue ?? "en";
        config.valueChange = this.outputLanguageChanged.bind(this, config);
    }

    async outputLanguageChanged(config: AiSettingConfig, newValue: string) {
        if (this.tab.showLoader) {
            return;
        }

        if (config.dropdownValue === newValue) {
            return;
        }

        await firstValueFrom(
            this.aiSettingsService.storeSetting({
                id: AiSettingsType.OutputLanguage,
                stringValue: newValue,
            }),
        );

        config.dropdownValue = newValue;
        this.toastr.success(this._translation.t("settings.outputLanguageUpdated"));
    }
}
